import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    TextField,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Button
} from '@mui/material';
import {
    AdminPanelSettings,
    Search,
    Refresh,
    Person,
    Email,
    CalendarToday,
    Logout
} from '@mui/icons-material';
import api from '../axios/axiosInstance';

const LotteryManagement = ({ onLogout }) => {
    const [teachers, setTeachers] = useState([]);
    const [filteredTeachers, setFilteredTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchTeachers();
    }, []);

    useEffect(() => {
        // Filter teachers based on search term
        if (searchTerm.trim() === '') {
            setFilteredTeachers(teachers);
        } else {
            const filtered = teachers.filter(teacher =>
                teacher.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                teacher.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                teacher.email.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredTeachers(filtered);
        }
    }, [searchTerm, teachers]);

    const fetchTeachers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/user-accounts/teachers/');
            setTeachers(response.data);
            setFilteredTeachers(response.data);
            setAlert({ show: true, type: 'success', message: `Loaded ${response.data.length} teachers` });
        } catch (error) {
            console.error('Error fetching teachers:', error);
            setAlert({
                show: true,
                type: 'error',
                message: error.response?.data?.detail || 'Failed to load teachers. You may not have permission.'
            });
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', p: 3 }}>
            {/* Header */}
            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={2}>
                            <AdminPanelSettings sx={{ fontSize: 40, color: 'white' }} />
                            <Box>
                                <Typography variant="h4" fontWeight="bold" color="white">
                                    Lottery Management
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                    View all registered teachers
                                </Typography>
                            </Box>
                        </Box>
                        <Tooltip title="Logout">
                            <IconButton onClick={onLogout} sx={{ color: 'white' }}>
                                <Logout />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </CardContent>
            </Card>

            {/* Alert */}
            {alert.show && (
                <Alert
                    severity={alert.type}
                    onClose={() => setAlert({ ...alert, show: false })}
                    sx={{ mb: 3 }}
                >
                    {alert.message}
                </Alert>
            )}

            {/* Search and Summary */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                        <TextField
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ flexGrow: 1, maxWidth: 500 }}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                        <Box display="flex" gap={2} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                Total: <strong>{filteredTeachers.length}</strong> of <strong>{teachers.length}</strong>
                            </Typography>
                            <Tooltip title="Refresh">
                                <IconButton onClick={fetchTeachers} color="primary">
                                    <Refresh />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Teachers Table */}
            <Card>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        All Teachers
                    </Typography>

                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} elevation={0}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell><strong>#</strong></TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Person fontSize="small" />
                                                <strong>First Name</strong>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Person fontSize="small" />
                                                <strong>Last Name</strong>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Email fontSize="small" />
                                                <strong>Email</strong>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <CalendarToday fontSize="small" />
                                                <strong>Date Signed Up</strong>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredTeachers.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">
                                                    {searchTerm ? 'No teachers found matching your search' : 'No teachers found'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredTeachers.map((teacher, index) => (
                                            <TableRow
                                                key={teacher.id}
                                                sx={{
                                                    '&:hover': { bgcolor: '#f9f9f9' },
                                                    '&:nth-of-type(odd)': { bgcolor: '#fafafa' }
                                                }}
                                            >
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{teacher.first_name}</TableCell>
                                                <TableCell>{teacher.last_name}</TableCell>
                                                <TableCell>{teacher.email}</TableCell>
                                                <TableCell>{formatDate(teacher.date_signed_up)}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default LotteryManagement;
